import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  ConfirmDialog,
  DataGrid,
  Modal,
  StatusBadge,
  useToast,
  type DataGridColumn,
} from '@kss/ui';
import { Plus, Trash2, Pencil, Sheet } from 'lucide-react';
import { encodeId } from '../../utils/idCodec';
import { AddDropdown, RefreshButton } from '../shared/AddDropdown';
import { NameCell } from '../../hooks/useNameMaps';
import { type CsvColumn } from '../shared/csv';
import { useCsvIO } from '../shared/useCsvIO';
import { useModulePerms } from '../../hooks/useModulePerms';
import type { Category, CategoryCreate } from '@kss/api';
import { categoryService } from '@kss/api';

const CSV_COLUMNS: CsvColumn<Category>[] = [
  { header: 'name', get: (r) => r.name },
  { header: 'description', get: (r) => r.description ?? '' },
  { header: 'sortOrder', get: (r) => r.sortOrder ?? '' },
  { header: 'isActive', get: (r) => (r.isActive ? 'true' : 'false') },
  { header: 'parentId', get: (r) => r.parentId ?? '' },
  { header: 'storeId', get: (r) => r.storeId ?? '' },
];

function csvRowToCreate(row: Record<string, string>): CategoryCreate | null {
  const name = (row.name ?? '').trim();
  if (!name) return null;
  const sortOrderRaw = row.sortOrder?.trim();
  const active = (row.isActive ?? '').trim().toLowerCase();
  const isFalse = ['0', 'false', 'no', 'n', 'inactive'].includes(active);
  return {
    name,
    description: row.description?.trim() || undefined,
    sortOrder: sortOrderRaw ? Number(sortOrderRaw) : undefined,
    isActive: !isFalse,
    parentId: row.parentId?.trim() || undefined,
    storeId: row.storeId?.trim() || undefined,
  };
}

export default function Categories() {
  const navigate = useNavigate();
  const toast = useToast();

  const [rows, setRows] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [detailRow, setDetailRow] = useState<Category | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await categoryService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load categories';
      setError(msg);
      toastRef.current.error('Load failed', msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete?.categoryId) return;
    setDeleting(true);
    try {
      await categoryService.delete(confirmDelete.categoryId);
      toast.success('Deleted', `${confirmDelete.name} removed`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error('Delete failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setDeleting(false);
    }
  };

  const csv = useCsvIO<Category, CategoryCreate>({
    filename: 'categories',
    columns: CSV_COLUMNS,
    rows,
    selected,
    rowId: (r) => r.categoryId,
    rowToCreate: csvRowToCreate,
    bulkCreate: (items) => categoryService.create(items),
    reload: load,
    noun: 'category',
    module: 'Category',
  });
  const perms = useModulePerms('Category');

  const columns: DataGridColumn<Category>[] = [
    { accessor: 'name', title: 'Name', sortable: true, filterable: true },
    {
      accessor: 'description', title: 'Description', sortable: true, filterable: true,
      render: (r) => r.description || '—',
    },
    {
      accessor: 'sortOrder', title: 'Sort', sortable: true, textAlign: 'right',
      render: (r) => r.sortOrder ?? 0,
    },
    {
      accessor: 'isActive', title: 'Status', sortable: true,
      render: (r) => <StatusBadge status={r.isActive ? 'active' : 'inactive'} />,
    },
    {
      accessor: 'categoryId', title: 'Actions', width: 110,
      render: (r) => (
        <div className="flex items-center gap-1">
          {perms.canUpdate && (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
              onClick={(e) => { e.stopPropagation(); if (r.categoryId) navigate(`/panel/categories/${encodeId(r.categoryId)}`); }}
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {perms.canDelete && (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
              onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }}
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {csv.fileInput}

      <DataGrid<Category>
        title={<span className="hidden sm:inline">All categories</span>}
        columnPicker
        onRowClick={(row) => setDetailRow(row)}
        data={rows}
        columns={columns}
        selectable
        selectedIds={selected}
        onSelectedChange={setSelected}
        defaultPageSize={10}
        pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <RefreshButton onClick={load} loading={loading} />
            <AddDropdown
              items={[
                ...(perms.canCreate
                  ? [{ label: 'New category', to: '/panel/categories/new', icon: <Plus className="h-3.5 w-3.5" /> }]
                  : []),
                ...(perms.canImport
                  ? [{ label: 'Bulk import (spreadsheet)', to: '/panel/categories/bulk', icon: <Sheet className="h-3.5 w-3.5" /> }]
                  : []),
                ...csv.menuItems,
              ]}
            />
          </div>
        }
      />

      <Modal
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        title={detailRow ? `Category · ${detailRow.name}` : ''}
        width={520}
        footer={
          <>
            <Button variant="outline" onClick={() => setDetailRow(null)}>Close</Button>
            {detailRow?.categoryId && (
              <Button onClick={() => { const id = detailRow.categoryId!; setDetailRow(null); navigate(`/panel/categories/${encodeId(id)}`); }}>
                Edit
              </Button>
            )}
          </>
        }
      >
        {detailRow && (
          <div className="grid gap-3 rounded-md border border-border bg-surface-card-hover p-4 text-sm">
            <DetailRow label="Name" value={detailRow.name} />
            <DetailRow label="Description" value={detailRow.description} />
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="Sort order" value={detailRow.sortOrder ?? 0} />
              <DetailRow label="Status" value={<StatusBadge status={detailRow.isActive ? 'active' : 'inactive'} />} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* Resolve FK IDs → names via NameCell so the detail
                  modal mirrors the grid (no raw GUIDs surfaced). */}
              <DetailRow label="Parent" value={<NameCell kind="category" id={detailRow.parentId} />} />
              <DetailRow label="Store" value={<NameCell kind="store" id={detailRow.storeId} />} />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        onConfirm={handleDelete}
        title="Delete category"
        message={confirmDelete ? `This will permanently remove "${confirmDelete.name}". This action cannot be undone.` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
      />
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">
        {value || <em className="text-content-muted">—</em>}
      </span>
    </div>
  );
}
